import { Inject, Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { UserModel } from '../../models/user.model';
import { environment } from '@environments/environment';
import { ResponseModel } from '@modules/auth/models/response';
import { UsersTable } from '@fake/users.table';
import { jwtDecode } from 'jwt-decode';
import { IdentityUser } from '@modules/auth/models/identity-user';
import { AuthModel } from '@modules/auth/models/auth.model';
import { SESSION_STORAGE } from '@donstudio/ds/_tokens';
import { Router } from '@angular/router';
import CryptoJS from "crypto-js";


const API_USERS_URL = `${environment.apiUrl}`;

@Injectable({
  providedIn: 'root',
})
export class AuthHTTPService {
  private authSessionStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;
  constructor(
    private http: HttpClient,
    @Inject(SESSION_STORAGE) private sessionStorage: Storage,
    private router: Router
  ) { }

  // public methods
  login(username: string, password: string): Observable<any> {
    return this.http.post<ResponseModel<{
      fromCache: boolean,
      token: string,
      refreshToken: string,
      sec: string,
      customerLogo: string,
      customerTitle: string,
      userTitle: string,
    }>>(`${API_USERS_URL}/api/auth/login`, {
      username,
      password: CryptoJS.MD5(password).toString(),
      service: 'moghimHotelSupplierPanel',
    }).pipe(
      map((result) => {
        if (result.isSuccess) {
          const token = result.data.token;
          const tokenInfo = this.getDecodedAccessToken(token);
          result?.data?.customerLogo && this.sessionStorage.setItem('customerLogo', result.data.customerLogo);
          result?.data?.customerTitle && this.sessionStorage.setItem('customerTitle', result.data.customerTitle);
          result?.data?.userTitle && this.sessionStorage.setItem('userTitle', result.data.userTitle);
          return {
            authToken: token,
            refreshToken: result.data.refreshToken,
            sec: result.data.sec,
            expiresIn: tokenInfo?.exp
          }
        }

        throw new Error('Valid token not returned');
      })
    );
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.getAuthFromSessionStorage()?.refreshToken;
    return this.http.post<ResponseModel<{ fromCache: boolean, token: string, refreshToken: string, sec: string }>>(`${API_USERS_URL}/api/auth/refreshToken`, {
      refreshToken: refreshToken
    }).pipe(
      map((result) => {
        if (result.isSuccess) {
          const token = result.data.token;
          const tokenInfo = this.getDecodedAccessToken(token);

          return {
            authToken: token,
            refreshToken: result.data.refreshToken,
            sec: result.data.sec,
            expiresIn: tokenInfo?.exp
          }
        }

        this.sessionStorage.removeItem(this.authSessionStorageToken);
        this.sessionStorage.removeItem('hotel');
        this.router.navigate(['/auth/login'], {
          queryParams: {},
        });
      })
    );
  }

  changePassword(oldPassword: string, newPassword: string): Observable<any> {
    return this.http.post<ResponseModel<{ fromCache: boolean, token: string, refreshToken: string, sec: string }>>(`${API_USERS_URL}/api/auth/resetPassword`, {
      oldPassword: CryptoJS.MD5(oldPassword).toString(),
      newPassword: CryptoJS.MD5(newPassword).toString(),
    }).pipe(
      map((result) => {
        if (result.isSuccess) {
          const token = result.data.token;
          const tokenInfo = this.getDecodedAccessToken(token);

          return {
            authToken: token,
            refreshToken: result.data.refreshToken,
            sec: result.data.sec,
            expiresIn: tokenInfo?.exp
          }
        }

        throw new Error('Valid token not returned');
      })
    );
  }

  // CREATE =>  POST: add a new user to the server
  createUser(user: UserModel): Observable<UserModel> {
    return this.http.post<UserModel>(API_USERS_URL, user);
  }

  // Your server should check email => If email exists send link to the user and return true | If email doesn't exist return false
  forgotPassword(email: string): Observable<boolean> {
    return this.http.post<boolean>(`${API_USERS_URL}/forgot-password`, {
      email,
    });
  }

  getUserByToken(token: string): Observable<UserModel> {
    const tokenInfo = this.getDecodedAccessToken(token);
    const FakeUser = UsersTable.users.find((user: UserModel) => user.id === 1);

    if (tokenInfo === null) {
      return of();
    }

    return of({
      ...FakeUser,
      id: +tokenInfo.id,
      fullname: tokenInfo.user_title,
      roles: [1],
      companyName: tokenInfo.customer_title,
    });
    // const httpHeaders = new HttpHeaders({
    //   Authorization: `Bearer ${token}`,
    // });
    // return this.http.get<UserModel>(`${API_USERS_URL}/me`, {
    //   headers: httpHeaders,
    // });
  }

  getDecodedAccessToken(token: string): IdentityUser | null {
    try {
      return jwtDecode<IdentityUser>(token);
    } catch (Error) {
      return null;
    }
  }

  private getAuthFromSessionStorage(): AuthModel | undefined {
    try {
      const lsValue = this.sessionStorage.getItem(this.authSessionStorageToken);
      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }
}
